<template>
  <div
    v-if="product"
    id="gift-card"
    class="gift-card"
  >
    <breadcrumbs
      class="container breadcrumb-container px20"
      :routes="[{ name: $t('Home'), route_link: '/' }]"
      :active-route="product.name"
    />
    <header class="row m0 c-bg-black">
      <div class="container">
        <h1 class="gift-card__title w-100 align-center fs22 lh27 c-white mt10 mb8">
          {{ product.name }}
        </h1>
      </div>
    </header>
    <div class="gift-card__wrapper container px20 pb40">
      <form
        novalidate
        class="gift-card__content"
        @submit.prevent="handleSubmit"
      >
        <div class="gift-card__preview">
          <img
            v-if="selectedImage"
            :src="imageUrl(selectedImage)"
            :alt="selectedImage.title"
            :width="imageWidth"
            :height="imageHeight"
            class="gift-card__preview-image"
          >
          <p class="gift-card__preview-amount">
            {{ form.amount | price }}
          </p>
          <p class="gift-card__preview-note">
            {{ $t('The gift card is sent by email and is valid for 12 months.') }}
          </p>
        </div>

        <div class="gift-card__form">
          <fieldset class="gift-card__section">
            <legend class="gift-card__label">
              {{ $t('Choose amount') }}
            </legend>
            <div class="gift-card__amounts">
              <button
                v-for="amount in amounts"
                :key="amount"
                type="button"
                class="gift-card__tile"
                :class="{ 'gift-card__tile--active': !form.isCustomAmount && form.amount === amount }"
                :aria-pressed="String(!form.isCustomAmount && form.amount === amount)"
                @click="selectAmount(amount)"
              >
                {{ amount | price }}
              </button>
              <button
                v-if="isOpenAmountAllowed"
                type="button"
                class="gift-card__tile"
                :class="{ 'gift-card__tile--active': form.isCustomAmount }"
                :aria-pressed="String(form.isCustomAmount)"
                @click="selectCustomAmount"
              >
                {{ $t('Other amount') }}
              </button>
            </div>
            <div
              v-if="form.isCustomAmount"
              class="gift-card__custom-amount"
            >
              <p
                :id="`${customAmountId}-hint`"
                class="gift-card__hint"
              >
                {{ amountHint }}
              </p>
              <div class="gift-card__amount-field">
                <input
                  :id="customAmountId"
                  :value="form.amount || ''"
                  :aria-labelledby="`${customAmountId}-hint`"
                  :maxlength="customAmountMaxLength"
                  class="gift-card__amount-input"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  name="amount"
                  @input="onCustomAmountInput"
                >
                <span class="gift-card__amount-suffix">{{ currencySign }}</span>
              </div>
              <p
                v-if="$v.form.amount.$error"
                class="gift-card__error"
                role="alert"
              >
                {{ amountHint }}
              </p>
            </div>
          </fieldset>

          <fieldset
            v-if="images.length"
            class="gift-card__section"
          >
            <legend class="gift-card__label">
              {{ $t('Choose design') }}
            </legend>
            <div class="gift-card__designs">
              <button
                v-for="image in images"
                :key="image.id"
                type="button"
                class="gift-card__design"
                :class="{ 'gift-card__design--active': form.imageId === image.id }"
                :aria-pressed="String(form.imageId === image.id)"
                :aria-label="image.title"
                @click="form.imageId = image.id"
              >
                <img
                  :src="imageUrl(image)"
                  :alt="image.title"
                  :width="imageWidth"
                  :height="imageHeight"
                  loading="lazy"
                >
              </button>
            </div>
          </fieldset>

          <fieldset class="gift-card__section">
            <legend class="gift-card__label">
              {{ $t('Recipient') }}
            </legend>
            <base-input
              v-model="form.recipientName"
              class="gift-card__field"
              name="recipientName"
              :maxlength="nameMaxLength"
              :placeholder="$t('Recipient name')"
              :validations="requiredValidations('recipientName')"
            />
            <base-input
              v-model="form.recipientEmail"
              class="gift-card__field"
              type="email"
              name="recipientEmail"
              :maxlength="emailMaxLength"
              :placeholder="$t('Recipient email')"
              :validations="emailValidations"
            />
          </fieldset>

          <fieldset class="gift-card__section">
            <legend class="gift-card__label">
              {{ $t('Greeting') }}
            </legend>
            <base-input
              v-model="form.senderName"
              class="gift-card__field"
              name="senderName"
              :maxlength="nameMaxLength"
              :placeholder="$t('Your name')"
              :validations="requiredValidations('senderName')"
            />
            <base-textarea
              v-model="form.message"
              class="gift-card__field"
              name="message"
              :placeholder="$t('Gift message (max 200 characters)')"
              :maxlength="messageMaxLength"
            />
          </fieldset>

          <button-full
            type="submit"
            class="gift-card__submit button button-secondary fs20 lh24 py16"
            :disabled="isSubmitting"
          >
            {{ $t('Buy gift card') }}
          </button-full>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { required, email, between, maxLength } from 'vuelidate/lib/validators'
import { localizedRoute, currentStoreView } from '@vue-storefront/core/lib/multistore'
import Breadcrumbs from 'theme/components/core/Breadcrumbs.vue'
import BaseInput from 'theme/components/core/blocks/Form/BaseInput.vue'
import BaseTextarea from 'theme/components/core/blocks/Form/BaseTextarea.vue'
import ButtonFull from 'theme/components/theme/ButtonFull.vue'
import { CheckoutRouteMixin } from 'src/modules/ab-test/mixins/checkoutRouteMixin'
import { defaultCheckoutRoute } from 'src/modules/ab-test/getCheckoutRoute'
import {
  AMOUNTS_FIELD,
  EMAIL_MAX_LENGTH,
  IMAGES_FIELD,
  IMAGE_HEIGHT,
  IMAGE_WIDTH,
  MESSAGE_MAX_LENGTH,
  NAME_MAX_LENGTH,
  OPEN_AMOUNT_ALLOWED_FIELD,
  OPEN_AMOUNT_MAX_FIELD,
  OPEN_AMOUNT_MIN_FIELD
} from 'src/modules/gift-card/constants'
import { buildGiftCardCartItem, getGiftCardImageUrl, isGiftCardAddedToCart } from 'src/modules/gift-card/helpers'

const NON_DIGIT_PATTERN = /\D/g

export default {
  name: 'GiftCard',
  components: {
    Breadcrumbs,
    BaseInput,
    BaseTextarea,
    ButtonFull
  },
  mixins: [CheckoutRouteMixin],
  data () {
    return {
      form: {
        amount: 0,
        isCustomAmount: false,
        imageId: null,
        senderName: '',
        recipientName: '',
        recipientEmail: '',
        message: ''
      },
      messageMaxLength: MESSAGE_MAX_LENGTH,
      imageWidth: IMAGE_WIDTH,
      imageHeight: IMAGE_HEIGHT,
      nameMaxLength: NAME_MAX_LENGTH,
      emailMaxLength: EMAIL_MAX_LENGTH,
      isSubmitting: false
    }
  },
  computed: {
    product () {
      return this.$store.getters['product/getCurrentProduct']
    },
    amounts () {
      return (this.product && this.product[AMOUNTS_FIELD]) || []
    },
    images () {
      return (this.product && this.product[IMAGES_FIELD]) || []
    },
    isOpenAmountAllowed () {
      return Boolean(this.product && Number(this.product[OPEN_AMOUNT_ALLOWED_FIELD]))
    },
    openAmountMin () {
      return Number(this.product && this.product[OPEN_AMOUNT_MIN_FIELD]) || 0
    },
    openAmountMax () {
      return Number(this.product && this.product[OPEN_AMOUNT_MAX_FIELD]) || 0
    },
    selectedImage () {
      return this.images.find(image => image.id === this.form.imageId) || null
    },
    customAmountMaxLength () {
      return String(this.openAmountMax).length
    },
    customAmountId () {
      return `gift-card-amount-${this._uid}`
    },
    currencySign () {
      return currentStoreView().i18n.currencySign
    },
    amountHint () {
      return this.$t('Amount between {min} and {max} kr', { min: this.openAmountMin, max: this.openAmountMax })
    },
    emailValidations () {
      return [
        {
          condition: this.$v.form.recipientEmail.$error && !this.$v.form.recipientEmail.required,
          text: this.$t('Field is required.')
        },
        {
          condition: this.$v.form.recipientEmail.$error && !this.$v.form.recipientEmail.email,
          text: this.$t('Please provide valid e-mail address.')
        }
      ]
    }
  },
  watch: {
    product: {
      immediate: true,
      handler () {
        this.resetSelection()
      }
    }
  },
  async asyncData ({ store, route, context }) {
    if (context) context.output.cacheTags.add('product')
    await store.dispatch('product/loadProduct', { parentSku: route.params.parentSku, childSku: null })
  },
  validations () {
    return {
      form: {
        amount: {
          required,
          between: this.form.isCustomAmount ? between(this.openAmountMin, this.openAmountMax) : () => true
        },
        recipientName: { required, maxLength: maxLength(NAME_MAX_LENGTH) },
        recipientEmail: { required, email, maxLength: maxLength(EMAIL_MAX_LENGTH) },
        senderName: { required, maxLength: maxLength(NAME_MAX_LENGTH) },
        message: { maxLength: maxLength(MESSAGE_MAX_LENGTH) }
      }
    }
  },
  methods: {
    resetSelection () {
      this.form.amount = this.amounts[0] || this.openAmountMin
      this.form.isCustomAmount = !this.amounts.length && this.isOpenAmountAllowed
      this.form.imageId = this.images.length ? this.images[0].id : null
    },
    selectAmount (amount) {
      this.form.isCustomAmount = false
      this.form.amount = amount
    },
    selectCustomAmount () {
      this.form.isCustomAmount = true
    },
    onCustomAmountInput (event) {
      const digits = event.target.value.replace(NON_DIGIT_PATTERN, '').slice(0, this.customAmountMaxLength)
      const amount = digits ? Math.min(Number(digits), this.openAmountMax) : 0

      event.target.value = amount || ''
      this.form.amount = amount
      this.$v.form.amount.$touch()
    },
    imageUrl (image) {
      return getGiftCardImageUrl(image)
    },
    requiredValidations (field) {
      return [{
        condition: this.$v.form[field].$error && !this.$v.form[field].required,
        text: this.$t('Field is required.')
      }]
    },
    async handleSubmit () {
      this.$v.$touch()

      if (this.$v.$invalid || this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      const productToAdd = buildGiftCardCartItem(this.product, { ...this.form, amount: Number(this.form.amount) })

      try {
        await this.$store.dispatch('cart/clear', { disconnect: false })
        const diffLog = await this.$store.dispatch('cart/addItem', { productToAdd })

        if (!isGiftCardAddedToCart(diffLog, this.$store.getters['cart/getCartItems'])) {
          this.notifyAddFailure()
          return
        }

        this.$router.push(localizedRoute(this.checkoutRoute || defaultCheckoutRoute(), currentStoreView().storeCode))
      } catch (error) {
        this.notifyAddFailure()
      } finally {
        this.isSubmitting = false
      }
    },
    notifyAddFailure () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('Could not add the gift card to the cart. Please try again.'),
        action1: { label: this.$t('OK') }
      })
    }
  },
  metaInfo () {
    return {
      title: this.product ? this.product.name : ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';
@import '~theme/css/variables/components';
@import '~theme/css/helpers/mixins/transition';

.gift-card {
  /deep/ .breadcrumbs {
    padding-left: 25px;

    a,
    i {
      color: color(white);
    }

    .current {
      color: color(other);
    }

    @include mobile {
      padding-left: 20px;
    }
  }

  &__wrapper {
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      width: 100%;
      height: 182px;
      background-color: color(black);
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 20px;
    border-radius: 10px;
    background-color: color(white);
    box-shadow: 0 2px 7px 0 rgba(color(black), 0.17);

    @include desktop {
      flex-direction: row;
      align-items: flex-start;
      padding: 40px 50px 30px;
      border-radius: 20px;
    }
  }

  @include desktop {
    .container.breadcrumb-container {
      padding-right: 84px;
      padding-left: 84px;
    }

    &__title.fs22 {
      margin-bottom: 35px;
      padding-top: 10px;
      font-size: 40px;
      line-height: 47px;
    }

    &__wrapper {
      padding-bottom: 50px;

      &.container {
        padding-right: 84px;
        padding-left: 84px;
      }

      &::before {
        height: 349px;
      }
    }
  }

  &__preview {
    min-width: 0;
    text-align: center;

    @include desktop {
      position: sticky;
      top: 20px;
      flex: 0 0 40%;
    }
  }

  &__preview-image {
    width: 100%;
    height: auto;
    border-radius: 10px;
  }

  &__preview-amount {
    margin: 15px 0 5px;
    overflow-wrap: anywhere;
    font-size: 28px;
    font-weight: 700;
  }

  &__preview-note {
    margin: 0;
    color: color(grayscale-dark);
  }

  &__form {
    flex: 1;
    min-width: 0;
  }

  &__section {
    margin: 0 0 30px;
    padding: 0;
    border: 0;
  }

  &__label {
    margin-bottom: 15px;
    font-size: 18px;
    font-weight: 700;
  }

  &__amounts {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;

    @include desktop {
      display: flex;
      flex-wrap: wrap;
    }
  }

  &__tile {
    min-width: 100px;
    padding: 12px 10px;
    border: 2px solid color(grayscale-other);
    border-radius: 3px;
    background-color: color(white);
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    touch-action: manipulation;
    user-select: none;

    @include transition(border-color, color, transform);

    @include mediaQuery(null, xsm) {
      font-size: 14px;
    }

    @include desktop {
      padding: 12px 20px;
    }

    &--active {
      border-color: color(primary);
      color: color(primary);
    }

    &:active {
      transform: scale(0.97);
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-color: color(primary);
        color: color(primary);
      }
    }
  }

  &__custom-amount {
    margin-top: 15px;
  }

  &__hint {
    margin: 0 0 8px;
    color: color(grayscale-dark);
  }

  &__amount-field {
    position: relative;
  }

  &__amount-input {
    width: 100%;
    padding: 12px 50px 12px 15px;
    border: 1px solid color(grayscale-light);
    font-size: 18px;
  }

  &__amount-suffix {
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
    font-size: 18px;
    pointer-events: none;
  }

  &__error {
    margin: 8px 0 0;
    color: color(error);
  }

  &__designs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;

    @include desktop {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  &__design {
    padding: 0;
    border: 3px solid transparent;
    border-radius: 6px;
    background: none;
    cursor: pointer;
    touch-action: manipulation;
    user-select: none;

    @include transition(border-color, transform);

    img {
      display: block;
      width: 100%;
      height: auto;
      border-radius: 3px;
    }

    &--active {
      border-color: color(primary);
    }

    &:active {
      transform: scale(0.97);
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-color: color(primary);
      }
    }
  }

  &__field {
    margin-bottom: 15px;
  }

  &__submit {
    width: 100%;
  }
}
</style>
